import {Discount, DiscountType, ValueOffType} from '@mfb/cookbook';
import {PromoBannerProps} from '@mfb/lego';
import { Discount2 } from './client';

/**
 * Format a number with leading zero if it's less than 10
 */
export const leading0 = (num: number): string => {
  return num < 10 ? '0' + num : num.toString();
};

/**
 * Calculate time remaining until a deadline
 */
export const getTimeRemaining = (deadline?: Date) => {
  if (!deadline) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const now = new Date();
  const time = Date.parse(deadline.toISOString()) - Date.parse(now.toISOString());
  
  if (time < 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  
  return {
    days: Math.floor(time / (1000 * 60 * 60 * 24)),
    hours: Math.floor((time / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((time / 1000 / 60) % 60),
    seconds: Math.floor((time / 1000) % 60)
  };
};

/**
 * Check if a promo code is enabled for countdown
 */
export const isPromoCountdownEnabled = (promoCode?: string): boolean => {
  if (!promoCode) return false;
  
  const promoList = (process.env.GATSBY_COUNTDOWN_PROMO || '')
    .split(',')
    .map(promo => promo.trim().toUpperCase());

  return promoList.includes(promoCode.toUpperCase());
};

/**
 * Get promo banner properties based on discount
 */
export const getPromoBannerProps = (discount?: Discount): PromoBannerProps => {
  // Return early if discount is invalid or not provided
  if (!discount || typeof discount !== 'object') {
    return { message: '', shouldDisplay: false };
  }

  // Safe type cast with null check
  const discountResponse = discount as Discount2;
  const isCountdownEnabled = isPromoCountdownEnabled(discount.code);
  // Safe access to redeemableTo
  const deadline = discountResponse && discountResponse.redeemableTo ? new Date(discountResponse.redeemableTo) : undefined;
  
  // Calculate time remaining
  const countdown = isCountdownEnabled && deadline ? getTimeRemaining(deadline) : {
    days: 0, hours: 0, minutes: 0, seconds: 0
  };

  const isZeroValue = discount.valueOff === 0;
  const isFreeProduct = discount.valueOffType === ValueOffType.FreeProduct;
  const hasDescription = discount.description && discount.description.length > 0;
  const shouldDisplay = (!isZeroValue && !isFreeProduct) || hasDescription;
  const isVoucher = discount.discountType === DiscountType.Voucher;

  let message = discount.description;

  if (!message || isVoucher) {
    let value = '';
    if (discount.valueOffType === ValueOffType.Percentage) {
      value = `${discount.valueOff}%`;
    } else {
      value = `$${discount.valueOff}`;
    }

    message = `Order today for ${value} off your first delivery. Simple...and not to mention delicious!`;
  }

  // Generate countdown text if applicable
  let countdownText = '';
  if (isCountdownEnabled && deadline) {
    if (countdown.days) {
      countdownText = `🏷️ Offer ends in ${leading0(countdown.days)} days, ${leading0(
        countdown.hours
      )} hrs, ${leading0(countdown.minutes)} mins`;
    } else {
      countdownText = `🏷️ Hurry, ends in ${leading0(countdown.hours)} hrs, ${leading0(
        countdown.minutes
      )} mins, ${leading0(countdown.seconds)} secs`;
    }
  }

  return { 
    message, 
    countdownText, 
    shouldDisplay 
  } as PromoBannerProps;
};